import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { LANGUAGES, type LanguageCode } from '../i18n/config';
import { useI18n } from '../i18n/I18nContext';

interface LanguageSwitcherProps {
  isScrolled?: boolean;
  mobile?: boolean;
}

function LanguageSwitcher({ isScrolled = false, mobile = false }: LanguageSwitcherProps) {
  const { lang, changeLanguage } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const current = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];

  const handleSelect = (code: LanguageCode) => {
    changeLanguage(code);
    setIsOpen(false);
  };

  if (mobile) {
    return (
      <div ref={ref} className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-2 w-full text-left px-3 py-2 rounded-md transition ${isScrolled ? 'text-zinc-800' : 'text-white'} hover:bg-zinc-700/30`}
          aria-label="Select language"
          aria-expanded={isOpen}
        >
          <Globe className="h-5 w-5" />
          <span>{current.nativeName}</span>
          <ChevronDown className={`h-4 w-4 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div className="mt-1 rounded-lg border border-zinc-700 bg-zinc-900 overflow-hidden shadow-xl">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => handleSelect(l.code)}
                className={`flex items-center justify-between w-full px-4 py-2.5 text-sm transition hover:bg-zinc-800 ${l.code === lang ? 'text-[#FDCB12]' : 'text-gray-300'}`}
              >
                <span>{l.nativeName}</span>
                {l.code === lang && <Check className="h-4 w-4" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 text-sm font-medium transition ${isScrolled ? 'text-zinc-800 hover:text-[#FF5500]' : 'text-white hover:text-[#FDCB12]'}`}
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        <Globe className="h-4 w-4" />
        <span className="hidden lg:inline">{current.nativeName}</span>
        <span className="lg:hidden">{current.code.toUpperCase()}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 rounded-lg border border-zinc-700 bg-zinc-900 overflow-hidden shadow-xl z-50 min-w-[160px]">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => handleSelect(l.code)}
              className={`flex items-center justify-between w-full px-4 py-2.5 text-sm transition hover:bg-zinc-800 ${l.code === lang ? 'text-[#FDCB12]' : 'text-gray-300'}`}
            >
              <span>{l.nativeName}</span>
              {l.code === lang && <Check className="h-4 w-4" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;
